/* eslint-disable jira/filename-case, jira/naming/disallow-filenames */
import { useMemo, useCallback, useRef } from 'react';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import queryString from 'query-string';
import { useShallowStableReference } from '@jira/platform__utils-stable-reference/src/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';

type SearchParams = {
	[key: string]: string;
};
type Options = {
	sort?: ((a: string, b: string) => number) | false;
	updateType?: 'push' | 'replace';
};

export const useQueryParams = (
	...unstableQueryParamsToWatch: string[]
): readonly [
	SearchParams,
	(
		newQueryParams: {
			[key: string]: string | null | undefined;
		},
		options?: Options,
	) => void,
] => {
	const [
		{
			location: { pathname },
			query,
		},
		{ push, replace },
	] = useRouter();
	const queryParamsRef = useRef({});

	// ensure queryParamsToWatch is not a new array on every invocation
	const queryParamsToWatch = useShallowStableReference(unstableQueryParamsToWatch);

	const parsedQueryParams = useMemo((): SearchParams => {
		// oxlint-disable-next-line @typescript-eslint/no-explicit-any
		const newQueryParams = queryParamsToWatch.reduce<Record<string, any>>(
			(accumulator, currentValue) => {
				accumulator[currentValue] = query[currentValue] || undefined;

				return accumulator;
			},
			{},
		);

		// update cache only when one of requested params actually changed
		// otherwise return cached value to avoid changing return object reference
		if (!isEqual(queryParamsRef.current, newQueryParams)) {
			queryParamsRef.current = newQueryParams;
		}

		return queryParamsRef.current;
	}, [query, queryParamsToWatch]);

	const setQueryParams = useCallback(
		(
			newQueryParams: {
				[key: string]: string | null | undefined;
			},
			options?: Options,
		) => {
			const parsedSearch = omitBy(
				{
					...query,
					...newQueryParams,
				},
				(value) => isNil(value),
			);
			const { updateType, ...stringifyOptions } = options ?? {};
			// @ts-expect-error - TS2559 - Type '{ sort?: false | ((a: string, b: string) => number) | undefined; }' has no properties in common with type 'StringifyOptions'.
			const strSearch = queryString.stringify(parsedSearch, stringifyOptions);
			const url = pathname + (strSearch !== '' ? `?${strSearch}` : '');
			updateType === 'replace' ? replace(url) : push(url);
		},
		[pathname, push, replace, query],
	);

	return [parsedQueryParams, setQueryParams] as const;
};
